import { DEFAULT_STREAK_RULES, STREAK_KINDS, STREAK_KIND_KEYS, type StreakRules } from '@london-life/contracts';
import { Label } from '@london-life/ui/components/label';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';

// Streaks (Lagos Life's 🔥): what two players can do together each London day to keep one going, and how many
// missed days a week are forgiven. Part of the content draft, published with it.
export function StreaksPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const rules = working?.streaks ?? DEFAULT_STREAK_RULES;
  const change = (fn: (rules: StreakRules) => void) =>
    editor.edit((content) => {
      content.streaks ??= structuredClone(DEFAULT_STREAK_RULES);
      fn(content.streaks);
    });
  return (
    <section>
      <PageHeader
        title="Streaks"
        description="Two players keep a streak going by each doing one thing together every London day before midnight. Choose what counts and how many missed days a week are forgiven. Plain messages, stickers and money never count. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="What counts">
            <h2 className="content-subtitle">What counts</h2>
            {STREAK_KIND_KEYS.map((kind) => {
              const on = rules.counts.includes(kind);
              return (
                <label key={kind} className="designer-check">
                  <Switch
                    checked={on}
                    aria-label={STREAK_KINDS[kind].label}
                    // at least one thing must count
                    disabled={on && rules.counts.length === 1}
                    onCheckedChange={(next) =>
                      change((draft) => {
                        draft.counts = next
                          ? STREAK_KIND_KEYS.filter((key) => key === kind || draft.counts.includes(key))
                          : draft.counts.filter((key) => key !== kind);
                      })
                    }
                  />{' '}
                  {STREAK_KINDS[kind].icon} {STREAK_KINDS[kind].label}
                </label>
              );
            })}
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Missed days">
            <h2 className="content-subtitle">Missed days</h2>
            <div className="admin-field">
              <Label htmlFor="streak-forgiven">Missed days forgiven each week</Label>
              <select
                id="streak-forgiven"
                className="content-select"
                value={rules.forgivenPerWeek}
                onChange={(event) => change((draft) => void (draft.forgivenPerWeek = Number(event.target.value)))}
              >
                {[0, 1, 2, 3].map((count) => (
                  <option key={count} value={count}>
                    {count === 0 ? 'None: a missed day ends it' : count}
                  </option>
                ))}
              </select>
              <small className="admin-muted">Weeks run Monday to Sunday, London time.</small>
            </div>
          </fieldset>
        </div>
      )}
    </section>
  );
}
